/**
 * ValuesTestingPage
 *
 * Admin-only page for comparing calculated candidate values against stored baselines.
 * Accessed from Proposal page via header button (super admin only).
 *
 * Flow:
 * 1. Testing store is populated before navigation (from Proposal page)
 * 2. This page reads "actual" values from testing store
 * 3. Fetches "expected" baseline from API
 * 4. Shows diff comparison
 * 5. "Set as Baseline" button updates expected with actual
 */

import {
  Badge,
  Box,
  Button,
  Card,
  Heading,
  HStack,
  Spinner,
  Table,
  Text,
  VStack,
} from '@chakra-ui/react';
import { ArrowLeft, Check, ChevronDown, ChevronRight, RefreshCw, Save, X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CashflowComparisonTable,
  type ComparisonStats,
  calculateComparisonStats,
  DiffCell,
  formatNumber,
  getComparisonSummary,
} from '~/components/testing';
import { toaster } from '~/components/ui/toaster';
import { type CandidateSummary, useTestingStore } from '~/hooks/useTestingStore';
import { useAppStore } from '~/store';
import type { CashFlow } from '~/types/candidate';

const API_URL = import.meta.env.VITE_API_URL || '';

interface BaselineResponse {
  baseline: {
    id: string;
    configurationId: string;
    expectedCandidates: CandidateSummary[] | null;
    baselineSetAt: string | null;
    createdAt: string;
  } | null;
}

interface CashflowBaselineResponse {
  baseline: {
    id: string;
    configurationId: string;
    /** All candidates' cashflows keyed by name */
    expectedCashflowsByCandidate: Record<string, CashFlow[]> | null;
    baselineSetAt: string | null;
    createdAt: string;
  } | null;
}

/**
 * Renders a list of optimization badges or "None" if empty
 */
const OptimizationBadges: React.FC<{
  optimizations: string[];
  colorPalette: string;
}> = ({ optimizations, colorPalette }) => {
  if (optimizations.length === 0) {
    return (
      <Text color="gray.500" fontStyle="italic">
        None
      </Text>
    );
  }
  return (
    <HStack gap={1}>
      {optimizations.map((opt, idx) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: optimizations list is static, won't reorder
        <Badge key={`${opt}-${idx}`} colorPalette={colorPalette}>
          {opt}
        </Badge>
      ))}
    </HStack>
  );
};

/**
 * Displays shape and applied optimizations - both saved (expected) and current (actual)
 */
const OptimizationHistoryDisplay: React.FC<{
  shapeId: string;
  currentOptimizations: string[];
  savedOptimizations?: string[];
}> = ({ shapeId, currentOptimizations, savedOptimizations }) => {
  const hasSaved = savedOptimizations !== undefined;
  const hasChanged =
    hasSaved &&
    (currentOptimizations.length !== savedOptimizations.length ||
      !currentOptimizations.every((opt, idx) => savedOptimizations[idx] === opt));

  return (
    <HStack gap={6} mb={4} fontSize="sm" flexWrap="wrap">
      <HStack gap={2}>
        <Text fontWeight="medium" color="gray.600">
          Shape:
        </Text>
        <Badge colorPalette="blue">{shapeId}</Badge>
      </HStack>

      {hasSaved ? (
        <>
          {/* Saved (Expected) */}
          <HStack gap={2}>
            <Text fontWeight="medium" color="gray.600">
              Saved:
            </Text>
            <OptimizationBadges optimizations={savedOptimizations} colorPalette="purple" />
          </HStack>

          {/* Current (Actual) */}
          <HStack gap={2}>
            <Text fontWeight="medium" color={hasChanged ? 'orange.600' : 'gray.600'}>
              Current:
            </Text>
            <OptimizationBadges
              optimizations={currentOptimizations}
              colorPalette={hasChanged ? 'orange' : 'green'}
            />
            {hasChanged && (
              <Badge colorPalette="orange" size="sm">
                Changed
              </Badge>
            )}
          </HStack>
        </>
      ) : (
        /* No baseline - just show current */
        <HStack gap={2}>
          <Text fontWeight="medium" color="gray.600">
            Optimizations:
          </Text>
          <OptimizationBadges optimizations={currentOptimizations} colorPalette="green" />
        </HStack>
      )}
    </HStack>
  );
};

export const ValuesTestingPage: React.FC = () => {
  const { projectId, configurationId } = useParams<{
    projectId: string;
    configurationId: string;
  }>();
  const navigate = useNavigate();
  const userInfo = useAppStore((s) => s.userInfo);
  const testingData = useTestingStore((s) => s.testingData);
  const clearTestingData = useTestingStore((s) => s.clearTestingData);

  const [expectedCandidates, setExpectedCandidates] = useState<CandidateSummary[] | null>(null);
  const [baselineSetAt, setBaselineSetAt] = useState<string | null>(null);
  /** All candidates' expected cashflows keyed by name */
  const [expectedCashflowsByCandidate, setExpectedCashflowsByCandidate] = useState<Record<
    string,
    CashFlow[]
  > | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Which candidate's cashflows are currently expanded (null = none) */
  const [expandedCandidate, setExpandedCandidate] = useState<string | null>(null);
  /** Hide failed candidates filter */
  const [hideFailed, setHideFailed] = useState(false);

  // Check if user is super admin
  const isSuperAdmin = userInfo?.isSuperAdmin ?? false;

  // Fetch both baselines on mount
  useEffect(() => {
    async function fetchBaselines() {
      if (!configurationId) return;

      try {
        setIsLoading(true);

        // Fetch both baselines in parallel
        const [candidateResponse, cashflowResponse] = await Promise.all([
          fetch(`${API_URL}/api/configurations/${configurationId}/baseline`, {
            credentials: 'include',
          }),
          fetch(`${API_URL}/api/configurations/${configurationId}/baseline/cashflows`, {
            credentials: 'include',
          }),
        ]);

        if (!candidateResponse.ok) {
          if (candidateResponse.status === 403) {
            setError('Access denied. Admin privileges required.');
            return;
          }
          throw new Error('Failed to fetch baseline');
        }

        const candidateData: BaselineResponse = await candidateResponse.json();
        setExpectedCandidates(candidateData.baseline?.expectedCandidates ?? null);
        setBaselineSetAt(candidateData.baseline?.baselineSetAt ?? null);

        if (cashflowResponse.ok) {
          const cashflowData: CashflowBaselineResponse = await cashflowResponse.json();
          setExpectedCashflowsByCandidate(
            cashflowData.baseline?.expectedCashflowsByCandidate ?? null
          );
        }
      } catch (err) {
        console.error('Error fetching baseline:', err);
        setError('Failed to load baseline data');
      } finally {
        setIsLoading(false);
      }
    }

    fetchBaselines();
  }, [configurationId]);

  // Handle set baseline - saves BOTH candidate summaries AND all cashflows
  const handleSetBaseline = async () => {
    if (!configurationId || !testingData) return;

    // Build object with all candidates' cashflows keyed by name
    const expectedCashflowsByCandidate: Record<string, CashFlow[]> = {};
    for (const candidate of testingData.candidatesWithCashflows) {
      expectedCashflowsByCandidate[candidate.name] = candidate.cashFlows;
    }

    try {
      setIsSaving(true);

      // Save sequentially to avoid inconsistent state if one fails
      // Step 1: Save candidate baseline first
      const candidateResponse = await fetch(
        `${API_URL}/api/configurations/${configurationId}/baseline`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            expectedCandidates: testingData.candidateSummaries,
          }),
        }
      );

      if (!candidateResponse.ok) {
        throw new Error('Failed to set candidate baseline');
      }

      const candidateData: BaselineResponse = await candidateResponse.json();

      // Step 2: Save cashflow baseline
      const cashflowResponse = await fetch(
        `${API_URL}/api/configurations/${configurationId}/baseline/cashflows`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ expectedCashflowsByCandidate }),
        }
      );

      if (!cashflowResponse.ok) {
        // Candidate baseline saved but cashflow failed - warn user of partial state
        setExpectedCandidates(candidateData.baseline?.expectedCandidates ?? null);
        setBaselineSetAt(candidateData.baseline?.baselineSetAt ?? null);
        toaster.create({
          title: 'Partial save',
          description: 'Candidate baseline saved, but cashflow baseline failed. Try saving again.',
          type: 'warning',
        });
        return;
      }

      const cashflowData: CashflowBaselineResponse = await cashflowResponse.json();

      // Both succeeded - update all state
      setExpectedCandidates(candidateData.baseline?.expectedCandidates ?? null);
      setBaselineSetAt(candidateData.baseline?.baselineSetAt ?? null);
      setExpectedCashflowsByCandidate(cashflowData.baseline?.expectedCashflowsByCandidate ?? null);

      toaster.create({
        title: 'Baseline saved',
        description: `Saved ${testingData.candidateSummaries.length} candidates with cashflows`,
        type: 'success',
      });
    } catch (err) {
      console.error('Error setting baseline:', err);
      toaster.create({
        title: 'Error',
        description: 'Failed to save baseline.',
        type: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Handle back navigation
  const handleBack = () => {
    clearTestingData();
    navigate(`/projects/${projectId}/proposal`);
  };

  // Calculate comprehensive comparison stats
  const comparisonStats: ComparisonStats | null = testingData
    ? calculateComparisonStats(testingData.candidateSummaries, expectedCandidates)
    : null;

  const overallStatus = comparisonStats?.overallStatus ?? 'no-baseline';

  // Sort candidates by total revenue (descending) and optionally filter failed
  const sortedCandidates = useMemo(() => {
    if (!testingData) return [];
    let candidates = [...testingData.candidateSummaries];
    // Filter out failed candidates if checkbox is checked
    if (hideFailed) {
      candidates = candidates.filter((c) => c.viable);
    }
    // Sort by total revenue descending (highest first)
    candidates.sort((a, b) => b.totalRevenue - a.totalRevenue);
    return candidates;
  }, [testingData, hideFailed]);

  // Calculate totals across all candidates (for high-level comparison)
  const totals = useMemo(() => {
    if (!testingData) return null;

    const currentCandidates = testingData.candidateSummaries;
    const savedCandidates = expectedCandidates ?? [];

    const sumField = (
      candidates: typeof currentCandidates,
      field: 'totalPrincipal' | 'totalInterest' | 'totalRevenue'
    ) => candidates.reduce((sum, c) => sum + c[field], 0);

    return {
      current: {
        principal: sumField(currentCandidates, 'totalPrincipal'),
        interest: sumField(currentCandidates, 'totalInterest'),
        revenue: sumField(currentCandidates, 'totalRevenue'),
        count: currentCandidates.length,
      },
      saved:
        savedCandidates.length > 0
          ? {
              principal: sumField(savedCandidates, 'totalPrincipal'),
              interest: sumField(savedCandidates, 'totalInterest'),
              revenue: sumField(savedCandidates, 'totalRevenue'),
              count: savedCandidates.length,
            }
          : null,
    };
  }, [testingData, expectedCandidates]);

  // Error state
  if (error) {
    return (
      <Box p={8}>
        <VStack gap={4}>
          <Text color="red.500">{error}</Text>
          <Button onClick={handleBack}>Go Back</Button>
        </VStack>
      </Box>
    );
  }

  // No testing data
  if (!testingData) {
    return (
      <Box p={8}>
        <VStack gap={4}>
          <Text>No testing data loaded. Please navigate from the Proposal page.</Text>
          <Button onClick={() => navigate('/')}>Go Home</Button>
        </VStack>
      </Box>
    );
  }

  // Access denied
  if (!isSuperAdmin) {
    return (
      <Box p={8}>
        <VStack gap={4}>
          <Text color="red.500">Access denied. Super admin privileges required.</Text>
          <Button onClick={handleBack}>Go Back</Button>
        </VStack>
      </Box>
    );
  }

  return (
    <Box p={6} w="full">
      {/* Header */}
      <HStack justify="space-between" mb={6}>
        <HStack gap={4}>
          <Button variant="ghost" size="sm" onClick={handleBack}>
            <ArrowLeft size={16} />
            Back to Proposal
          </Button>
          <VStack align="start" gap={0}>
            <Heading size="lg">Values Testing</Heading>
            <Text color="gray.500" fontSize="sm">
              {testingData.projectName} / {testingData.configurationName}
            </Text>
          </VStack>
        </HStack>

        <HStack gap={4}>
          {/* Status Badge */}
          <Badge
            colorPalette={
              overallStatus === 'pass'
                ? 'green'
                : overallStatus === 'fail'
                  ? 'red'
                  : overallStatus === 'partial'
                    ? 'orange'
                    : 'yellow'
            }
            size="lg"
            p={2}
          >
            <HStack gap={1}>
              {overallStatus === 'pass' && <Check size={14} />}
              {overallStatus === 'fail' && <X size={14} />}
              {overallStatus === 'partial' && <RefreshCw size={14} />}
              {overallStatus === 'no-baseline' && <RefreshCw size={14} />}
              <Text>
                {comparisonStats ? getComparisonSummary(comparisonStats) : 'No Baseline Set'}
              </Text>
            </HStack>
          </Badge>

          {/* Set Baseline Button */}
          <Button
            colorPalette="blue"
            onClick={handleSetBaseline}
            loading={isSaving}
            disabled={isSaving}
          >
            <Save size={16} />
            {expectedCandidates ? 'Update Baseline' : 'Set as Baseline'}
          </Button>
        </HStack>
      </HStack>

      {/* Baseline Info & Filters */}
      <HStack justify="space-between" mb={4}>
        {baselineSetAt && (
          <Text fontSize="sm" color="gray.500">
            Baseline last set: {new Date(baselineSetAt).toLocaleString()}
          </Text>
        )}
        <Button
          variant={hideFailed ? 'solid' : 'outline'}
          colorPalette={hideFailed ? 'blue' : 'gray'}
          size="sm"
          onClick={() => setHideFailed(!hideFailed)}
        >
          {hideFailed ? 'Showing viable only' : 'Hide failed'}
        </Button>
      </HStack>

      {/* Totals Summary */}
      {totals && !isLoading && (
        <Card.Root mb={4} bg="gray.50">
          <Card.Body py={3}>
            <HStack gap={8} justify="center" flexWrap="wrap">
              {/* Candidate Count */}
              <VStack gap={0}>
                <Text fontSize="xs" color="gray.500" fontWeight="medium">
                  Candidates
                </Text>
                <HStack gap={2}>
                  {totals.saved && (
                    <>
                      <Text fontSize="sm" color="purple.600">
                        {totals.saved.count}
                      </Text>
                      <Text fontSize="sm" color="gray.400">
                        →
                      </Text>
                    </>
                  )}
                  <Text
                    fontSize="sm"
                    fontWeight="bold"
                    color={
                      totals.saved && totals.saved.count !== totals.current.count
                        ? 'orange.600'
                        : 'gray.700'
                    }
                  >
                    {totals.current.count}
                  </Text>
                </HStack>
              </VStack>

              {/* Total Principal */}
              <VStack gap={0}>
                <Text fontSize="xs" color="gray.500" fontWeight="medium">
                  Total Principal
                </Text>
                <HStack gap={2}>
                  {totals.saved && (
                    <>
                      <Text fontSize="sm" color="purple.600">
                        {formatNumber(totals.saved.principal)}
                      </Text>
                      <Text fontSize="sm" color="gray.400">
                        →
                      </Text>
                    </>
                  )}
                  <Text
                    fontSize="sm"
                    fontWeight="bold"
                    color={
                      totals.saved && totals.saved.principal !== totals.current.principal
                        ? 'orange.600'
                        : 'gray.700'
                    }
                  >
                    {formatNumber(totals.current.principal)}
                  </Text>
                </HStack>
              </VStack>

              {/* Total Interest */}
              <VStack gap={0}>
                <Text fontSize="xs" color="gray.500" fontWeight="medium">
                  Total Interest
                </Text>
                <HStack gap={2}>
                  {totals.saved && (
                    <>
                      <Text fontSize="sm" color="purple.600">
                        {formatNumber(totals.saved.interest)}
                      </Text>
                      <Text fontSize="sm" color="gray.400">
                        →
                      </Text>
                    </>
                  )}
                  <Text
                    fontSize="sm"
                    fontWeight="bold"
                    color={
                      totals.saved && totals.saved.interest !== totals.current.interest
                        ? 'orange.600'
                        : 'gray.700'
                    }
                  >
                    {formatNumber(totals.current.interest)}
                  </Text>
                </HStack>
              </VStack>

              {/* Total Revenue */}
              <VStack gap={0}>
                <Text fontSize="xs" color="gray.500" fontWeight="medium">
                  Total Revenue
                </Text>
                <HStack gap={2}>
                  {totals.saved && (
                    <>
                      <Text fontSize="sm" color="purple.600">
                        {formatNumber(totals.saved.revenue)}
                      </Text>
                      <Text fontSize="sm" color="gray.400">
                        →
                      </Text>
                    </>
                  )}
                  <Text
                    fontSize="sm"
                    fontWeight="bold"
                    color={
                      totals.saved && totals.saved.revenue !== totals.current.revenue
                        ? 'orange.600'
                        : 'gray.700'
                    }
                  >
                    {formatNumber(totals.current.revenue)}
                  </Text>
                </HStack>
              </VStack>
            </HStack>
          </Card.Body>
        </Card.Root>
      )}

      {/* Loading State */}
      {isLoading ? (
        <HStack justify="center" py={8}>
          <Spinner />
          <Text>Loading baseline...</Text>
        </HStack>
      ) : (
        /* Comparison Table */
        <Card.Root>
          <Card.Body p={0}>
            <Box overflowX="auto">
              <Table.Root size="sm">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader>Status</Table.ColumnHeader>
                    <Table.ColumnHeader>Candidate</Table.ColumnHeader>
                    <Table.ColumnHeader>Viable</Table.ColumnHeader>
                    <Table.ColumnHeader>Total Principal</Table.ColumnHeader>
                    <Table.ColumnHeader>Total Interest</Table.ColumnHeader>
                    <Table.ColumnHeader>Total Revenue</Table.ColumnHeader>
                    <Table.ColumnHeader>Utilization</Table.ColumnHeader>
                    <Table.ColumnHeader>Efficiency</Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {sortedCandidates.map((actual, index) => {
                    const expected = expectedCandidates?.find((e) => e.name === actual.name);
                    const comparison = comparisonStats?.matched
                      .concat(comparisonStats.mismatched)
                      .concat(comparisonStats.newCandidates)
                      .find((c) => c.name === actual.name);
                    const rowStatus = comparison?.status ?? 'new';
                    const isExpanded = expandedCandidate === actual.name;
                    const candidateCashflows = testingData.candidatesWithCashflows.find(
                      (c) => c.name === actual.name
                    );
                    const expectedCashflows = expectedCashflowsByCandidate?.[actual.name] ?? null;

                    return (
                      <React.Fragment key={actual.name}>
                        <Table.Row
                          cursor="pointer"
                          onClick={() => setExpandedCandidate(isExpanded ? null : actual.name)}
                          _hover={{ bg: 'gray.50' }}
                        >
                          <Table.Cell>
                            <HStack gap={1}>
                              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                              <Badge
                                size="sm"
                                colorPalette={
                                  rowStatus === 'matched'
                                    ? 'green'
                                    : rowStatus === 'mismatched'
                                      ? 'red'
                                      : 'yellow'
                                }
                              >
                                {rowStatus === 'matched' && <Check size={10} />}
                                {rowStatus === 'mismatched' && <X size={10} />}
                                {rowStatus === 'new' && 'NEW'}
                              </Badge>
                            </HStack>
                          </Table.Cell>
                          <Table.Cell fontWeight="medium">
                            <HStack gap={2}>
                              <Text>{index + 1}.</Text>
                              <Text>{actual.name}</Text>
                            </HStack>
                          </Table.Cell>
                          <Table.Cell>
                            {actual.viable ? (
                              <Text color="green.600" fontSize="sm">
                                Yes
                              </Text>
                            ) : (
                              <Badge colorPalette="red" size="sm">
                                No
                              </Badge>
                            )}
                          </Table.Cell>
                          <DiffCell
                            actual={actual.totalPrincipal}
                            expected={expected?.totalPrincipal}
                            format={(v) => formatNumber(v as number)}
                          />
                          <DiffCell
                            actual={actual.totalInterest}
                            expected={expected?.totalInterest}
                            format={(v) => formatNumber(v as number)}
                          />
                          <DiffCell
                            actual={actual.totalRevenue}
                            expected={expected?.totalRevenue}
                            format={(v) => formatNumber(v as number)}
                          />
                          <DiffCell
                            actual={actual.revenueUtilization}
                            expected={expected?.revenueUtilization}
                            format={(v) => (v as number).toFixed(3)}
                          />
                          <DiffCell
                            actual={actual.efficiency}
                            expected={expected?.efficiency}
                            format={(v) => (v as number).toFixed(3)}
                          />
                        </Table.Row>
                        {/* Expanded candidate details row */}
                        {isExpanded && candidateCashflows && (
                          <Table.Row>
                            <Table.Cell colSpan={8} p={0} bg="gray.50">
                              <Box p={4}>
                                {/* Optimization History */}
                                <OptimizationHistoryDisplay
                                  shapeId={actual.shapeId}
                                  currentOptimizations={actual.appliedOptimizations ?? []}
                                  savedOptimizations={expected?.appliedOptimizations}
                                />
                                {/* Cashflow Comparison */}
                                <CashflowComparisonTable
                                  candidateName={actual.name}
                                  actualCashflows={candidateCashflows.cashFlows}
                                  expectedCashflows={expectedCashflows}
                                />
                              </Box>
                            </Table.Cell>
                          </Table.Row>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {/* Missing candidates (in baseline but not in actual) */}
                  {comparisonStats?.missing.map((missing) => (
                    <Table.Row key={`missing-${missing.name}`} bg="purple.50">
                      <Table.Cell>
                        <Badge size="sm" colorPalette="purple">
                          MISSING
                        </Badge>
                      </Table.Cell>
                      <Table.Cell fontWeight="medium" color="purple.700">
                        <Text>{missing.name}</Text>
                      </Table.Cell>
                      <Table.Cell color="gray.500">
                        {missing.expected.viable ? 'Yes' : 'No'}
                      </Table.Cell>
                      <Table.Cell color="gray.500">
                        {formatNumber(missing.expected.totalPrincipal)}
                      </Table.Cell>
                      <Table.Cell color="gray.500">
                        {formatNumber(missing.expected.totalInterest)}
                      </Table.Cell>
                      <Table.Cell color="gray.500">
                        {formatNumber(missing.expected.totalRevenue)}
                      </Table.Cell>
                      <Table.Cell color="gray.500">
                        {missing.expected.revenueUtilization.toFixed(3)}
                      </Table.Cell>
                      <Table.Cell color="gray.500">
                        {missing.expected.efficiency.toFixed(3)}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </Box>
          </Card.Body>
        </Card.Root>
      )}

      {/* Legend */}
      <HStack gap={6} mt={4} flexWrap="wrap">
        <HStack gap={2}>
          <Box w={4} h={4} bg="green.50" border="1px solid" borderColor="green.200" />
          <Text fontSize="sm">Match</Text>
        </HStack>
        <HStack gap={2}>
          <Box w={4} h={4} bg="red.50" border="1px solid" borderColor="red.200" />
          <Text fontSize="sm">Mismatch</Text>
        </HStack>
        <HStack gap={2}>
          <Box w={4} h={4} bg="yellow.50" border="1px solid" borderColor="yellow.200" />
          <Text fontSize="sm">New (no baseline)</Text>
        </HStack>
        <HStack gap={2}>
          <Box w={4} h={4} bg="purple.50" border="1px solid" borderColor="purple.200" />
          <Text fontSize="sm">Missing (in baseline only)</Text>
        </HStack>
      </HStack>
    </Box>
  );
};

export default ValuesTestingPage;
